import { Clock } from 'lucide-react';
import React from 'react';
import { format, isToday, parseISO } from 'date-fns';
import { GlobalCalendarEvent, EVENT_TYPE_COLORS } from '../../types/calendar';

interface WeekViewProps {
  date: Date;
  events: GlobalCalendarEvent[];
  onEventClick: (event: GlobalCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  isLoading?: boolean;
}

const WeekView: React.FC<WeekViewProps> = ({
  date,
  events,
  onEventClick,
  onDateClick,
  isLoading,
}) => {
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    return day;
  });

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForDayAndHour = (day: Date, hour: number) => {
    return events.filter(event => {
      const start = parseISO(event.startDate);
      return start.toDateString() === day.toDateString() && start.getHours() === hour;
    });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 p-3 h-10" />
          {days.map((_, i) => (
            <div key={i} className="bg-gray-50 p-3 h-10" />
          ))}
          {Array.from({ length: 48 }).map((_, i) => (
            <div key={i} className="bg-white h-[60px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
      {/* Time column header */}
      <div className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700 sticky top-0 z-10">
        Time
      </div>

      {/* Day headers */}
      {days.map(day => (
        <div key={day.toISOString()} className="bg-gray-50 p-3 text-center sticky top-0 z-10">
          <div className="text-sm font-medium text-gray-900">{format(day, 'EEE')}</div>
          <div
            className={`text-sm ${isToday(day) ? 'text-brand-primary font-semibold' : 'text-gray-600'}`}
          >
            {format(day, 'd')}
          </div>
        </div>
      ))}

      {/* Time slots */}
      {hours.map(hour => (
        <React.Fragment key={hour}>
          <div className="bg-white p-2 text-xs text-gray-600 border-r border-gray-100">
            {String(hour).padStart(2, '0')}:00
          </div>

          {days.map(day => {
            const hourEvents = getEventsForDayAndHour(day, hour);
            const timeSlotDate = new Date(day);
            timeSlotDate.setHours(hour, 0, 0, 0);

            return (
              <div
                key={`${day.toISOString()}-${hour}`}
                className="bg-white min-h-[60px] p-1 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onDateClick?.(timeSlotDate)}
              >
                {hourEvents.map(event => {
                  const colors = EVENT_TYPE_COLORS[event.type];
                  return (
                    <button
                      key={event.id}
                      onClick={e => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      className={`w-full text-left p-1 text-xs ${colors.bg} ${colors.text} rounded hover:opacity-80 transition-opacity mb-1`}
                      title={`${event.title} - ${event.projectName}`}
                    >
                      <div className="font-medium truncate">{event.title}</div>
                      <div className="flex items-center space-x-1 opacity-75">
                        <Clock className="h-2.5 w-2.5" />
                        <span>{format(parseISO(event.startDate), 'HH:mm')}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

export default WeekView;
